import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Tooltip from "components/Tooltip";
import { batchesService } from "../../../services/metalguard";
import { supabase } from "../../../lib/supabase";

const fmt = (val, decimals = 2) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })?.format(val);

const fmtNum = (val, decimals = 2) =>
  new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })?.format(val);

function getRiskBadge(negotiatedTotal, totalBatchPrice) {
  if (!negotiatedTotal || !totalBatchPrice || negotiatedTotal <= totalBatchPrice) return null;
  const ratio = negotiatedTotal / totalBatchPrice;
  if (ratio <= 1.02) return "ACEPTABLE";
  if (ratio <= 1.05) return "ELEVADO";
  return "CRÍTICO";
}

const BADGE_CONFIG = {
  ACEPTABLE: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    dot: "bg-amber-500",
    icon: "AlertTriangle",
    iconColor: "#d97706",
    cardBg: "bg-amber-50",
    cardBorder: "border-amber-200",
  },
  ELEVADO: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
    dot: "bg-orange-500",
    icon: "AlertOctagon",
    iconColor: "#ea580c",
    cardBg: "bg-orange-50",
    cardBorder: "border-orange-200",
  },
  CRÍTICO: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    dot: "bg-red-500",
    icon: "XCircle",
    iconColor: "#dc2626",
    cardBg: "bg-red-50",
    cardBorder: "border-red-200",
  },
};

export default function NegotiatedPriceSection({ result, formData }) {
  const navigate = useNavigate();
  const [negotiatedPrice, setNegotiatedPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [batchName, setBatchName] = useState("");
  const [showNameInput, setShowNameInput] = useState(false);
  const [negPriceError, setNegPriceError] = useState("");
  const [negPriceWarning, setNegPriceWarning] = useState("");

  if (!result?.maxPurchasePrice) return null;

  const maxBuyPrice = parseFloat(result?.maxPurchasePrice) || 0;
  const totalBatchPrice = parseFloat(result?.totalBatchPrice) || 0;
  const netTons = (parseFloat(result?.netWeight) || 0) / 1000;

  // negotiatedPrice is now total lot price in €
  const negTotal = parseFloat(negotiatedPrice);
  const hasNegPrice = negotiatedPrice !== "" && !isNaN(negTotal) && negTotal > 0;

  // Convert negotiated total to €/t for internal calculations
  const negPricePerTon = hasNegPrice && netTons > 0 ? negTotal / netTons : 0;

  // ── Risk calculations (all in total €) ──────────────────────────────────────
  const isAboveCeiling = hasNegPrice && negTotal > totalBatchPrice;
  const isBelowCeiling = hasNegPrice && negTotal <= totalBatchPrice;

  const riskBadge = hasNegPrice ? getRiskBadge(negTotal, totalBatchPrice) : null;
  const badgeCfg = riskBadge ? BADGE_CONFIG?.[riskBadge] : null;

  // Below ceiling metrics (total €)
  const additionalMargin = isBelowCeiling ? totalBatchPrice - negTotal : null;
  // Also in €/t for reference
  const additionalMarginPerTon = additionalMargin != null && netTons > 0 ? additionalMargin / netTons : null;

  // Above ceiling metrics (total €)
  const riesgoAsumidoTotal = isAboveCeiling ? negTotal - totalBatchPrice : null;
  const riesgoAsumidoPerTon = riesgoAsumidoTotal != null && netTons > 0 ? riesgoAsumidoTotal / netTons : null;

  const targetMarginEur = parseFloat(result?.marginDeduction) || 0; // €/t
  const targetMarginTotal = targetMarginEur * netTons; // total €
  const margenRealResultante = isAboveCeiling ? targetMarginTotal - riesgoAsumidoTotal : null;
  const margenRealResultantePerTon = margenRealResultante != null && netTons > 0 ? margenRealResultante / netTons : null;

  const riskBufferDeduction = parseFloat(result?.riskBufferDeduction) || 0;
  const riskBufferTotal = riskBufferDeduction * netTons;
  const bufferConsumido =
    isAboveCeiling && riskBufferTotal > 0
      ? Math.min(100, (riesgoAsumidoTotal / riskBufferTotal) * 100)
      : null;

  // Punto de equilibrio (benchmark €/t at which lot breaks even)
  const yieldFactor = (parseFloat(result?.yieldPct) || 90) / 100;
  const riskBufferPct = (parseFloat(result?.riskBufferPct) || 0) / 100;
  const targetMargin = parseFloat(result?.targetMargin) || 0;
  const marginPct = targetMargin / 100;
  const factor = 1 - (1 - yieldFactor) - marginPct - riskBufferPct * yieldFactor;
  const logistics = parseFloat(result?.costLogistics) || 0;
  const processing = parseFloat(result?.costProcessing) || 0;
  const puntoEquilibrio =
    isAboveCeiling && factor > 0 && negPricePerTon > 0
      ? (negPricePerTon + logistics + processing) / factor
      : null;

  // ── Save handler ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!hasNegPrice) return;
    if (!batchName?.trim()) {
      setShowNameInput(true);
      return;
    }
    setSaving(true);
    try {
      const grossKg = parseFloat(formData?.weightGross) || 0;
      const tareKg = parseFloat(formData?.weightTare) || 0;
      const netKg = Math.max(0, grossKg - tareKg);

      const batchPayload = {
        batch_code: batchName?.trim() || `L-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`,
        metal_id: formData?.metalId || null,
        category_id: formData?.categoryId || null,
        metal_name: formData?.metal || "",
        category_name: formData?.category || "",
        weight_t: netKg / 1000,
        yield_est: formData?.yieldPct,
        logistics_cost: parseFloat(formData?.costLogistics) || 0,
        processing_cost: parseFloat(formData?.costProcessing) || 0,
        processing_days: formData?.daysToSale,
        target_margin: formData?.targetMargin,
        max_buy_price: result?.maxPurchasePrice,
        actual_buy_price: negPricePerTon,
        expected_margin: result?.targetMargin,
        purchase_price: negPricePerTon,
        notes: null,
        batch_status: "En Espera",
        progress: 0,
        ...(isAboveCeiling && {
          risk_override_eur_per_ton: riesgoAsumidoPerTon,
          risk_override_badge: riskBadge,
        }),
      };

      const { data: savedBatch, error } = await batchesService?.create(batchPayload);
      if (error) throw error;

      // Audit log for risk override
      if (isAboveCeiling && savedBatch?.id) {
        const { id: userId, email: userEmail } = await (async () => {
          const { data: { user } } = await supabase?.auth?.getUser();
          return { id: user?.id, email: user?.email };
        })();

        await supabase?.from('audit_log')?.insert({
          user_id: userId,
          user_email: userEmail,
          action: 'RISK_OVERRIDE',
          entity_type: 'batches',
          entity_id: String(savedBatch?.id),
          old_values: { max_buy_price: result?.maxPurchasePrice, total_batch_price: totalBatchPrice },
          new_values: {
            actual_buy_price: negPricePerTon,
            negotiated_total_eur: negTotal,
            risk_override_eur_per_ton: riesgoAsumidoPerTon,
            risk_override_total_eur: riesgoAsumidoTotal,
            risk_override_badge: riskBadge,
            buffer_consumido_pct: bufferConsumido,
            punto_equilibrio: puntoEquilibrio,
          },
          screen: 'batch-calculator',
        });
      }

      // Toast message
      const toastMsg = isAboveCeiling
        ? `Lote guardado — riesgo asumido: ${fmt(riesgoAsumidoTotal)} sobre el Total Lote (${riskBadge})`
        : `Lote guardado con precio negociado de ${fmt(negTotal)}`;

      setToast({ type: "success", msg: toastMsg });
      setTimeout(() => {
        setToast(null);
        navigate("/active-batch-management");
      }, 2000);
    } catch (err) {
      setToast({ type: "error", msg: err?.message || "Error al guardar el lote" });
      setTimeout(() => setToast(null), 3500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 md:p-5 flex flex-col gap-4">
      {/* Toast */}
      {toast && (
        <div
          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
            toast?.type === "success" ?"bg-emerald-50 border border-emerald-200 text-emerald-700" :"bg-red-50 border border-red-200 text-red-700"
          }`}
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          <Icon
            name={toast?.type === "success" ? "CheckCircle" : "AlertCircle"}
            size={15}
            color={toast?.type === "success" ? "#059669" : "#dc2626"}
          />
          {toast?.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: "var(--color-muted)" }}
        >
          <Icon name="Handshake" size={16} color="var(--color-accent)" strokeWidth={2} />
        </div>
        <div>
          <p
            className="text-sm font-semibold"
            style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Precio de Compra Negociado
          </p>
          <p
            className="text-xs"
            style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Total Lote máximo:{" "}
            <span className="font-data font-semibold" style={{ color: "var(--color-accent)" }}>
              {fmt(totalBatchPrice)}
            </span>
          </p>
        </div>
      </div>

      {/* Input */}
      <div>
        <label
          className="block text-xs font-medium mb-1.5"
          style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          <Tooltip
            title="Precio de Compra Negociado (Total Lote)"
            description="Importe total real al que decides comprar el lote tras la negociación. Puede ser superior al máximo calculado — el sistema cuantifica exactamente el riesgo adicional que estás asumiendo."
            example="Máximo calculado: 4.200 € → Negociado: 4.500 € → Riesgo asumido: 300 €"
          >
            Precio de Compra Negociado — Total Lote (€)
          </Tooltip>
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="Introduce el precio total negociado del lote..."
          value={negotiatedPrice}
          onChange={(e) => {
            const val = e?.target?.value;
            setNegotiatedPrice(val);
            const num = parseFloat(val);
            // Validate: negative
            if (val !== '' && !isNaN(num) && num < 0) {
              setNegPriceError('El precio negociado no puede ser negativo');
              setNegPriceWarning('');
            } else {
              setNegPriceError('');
              // Soft warning: value > maxBuyPrice * 2
              if (val !== '' && !isNaN(num) && num > 0 && maxBuyPrice > 0 && num > maxBuyPrice * 2) {
                setNegPriceWarning('El precio negociado parece excesivamente alto. ¿Es correcto?');
              } else {
                setNegPriceWarning('');
              }
            }
          }}
          className="w-full px-3 py-2.5 rounded-lg border text-sm"
          style={{
            borderColor: negPriceError ? "var(--color-error)" : isAboveCeiling ? "var(--color-error)" : isBelowCeiling ? "var(--color-success)" : "var(--color-border)",
            background: "var(--color-card)",
            color: "var(--color-text-primary)",
            fontFamily: "'Roboto Mono', monospace",
            outline: "none",
            boxShadow: negPriceError ? '0 0 0 2px rgba(220,38,38,0.15)' : 'none',
          }}
        />
        {negPriceError && (
          <p className="text-xs mt-1 text-red-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{negPriceError}</p>
        )}
        {!negPriceError && negPriceWarning && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-lg border" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }}>
            <Icon name="AlertTriangle" size={13} color="var(--color-warning)" strokeWidth={2} />
            <p className="text-xs" style={{ color: 'var(--color-warning)', fontFamily: "'IBM Plex Sans', sans-serif" }}>{negPriceWarning}</p>
          </div>
        )}
        {hasNegPrice && netTons > 0 && (
          <p className="text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Equivale a{" "}
            <span className="font-data font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {fmtNum(negPricePerTon)} €/t
            </span>
          </p>
        )}
      </div>

      {/* ── Below ceiling: green card ── */}
      {isBelowCeiling && (
        <div
          className="rounded-xl p-4 border border-emerald-200 bg-emerald-50 flex flex-col gap-2"
        >
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={16} color="#059669" strokeWidth={2.5} />
            <span
              className="text-sm font-semibold text-emerald-700"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              Dentro del Total Lote máximo ✓
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span
              className="text-xs text-emerald-600"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              Margen de seguridad adicional
            </span>
            <span
              className="font-data font-bold text-emerald-700 text-sm"
            >
              +{fmt(additionalMargin)}
            </span>
          </div>
          {additionalMarginPerTon != null && (
            <p
              className="text-xs text-emerald-600"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {fmt(additionalMargin)} de margen adicional sobre el Total Lote ({fmtNum(additionalMarginPerTon)} €/t)
            </p>
          )}
        </div>
      )}

      {/* ── Above ceiling: risk card ── */}
      {isAboveCeiling && riskBadge && badgeCfg && (
        <div
          className={`rounded-xl p-4 border flex flex-col gap-3 ${badgeCfg?.cardBg} ${badgeCfg?.cardBorder}`}
        >
          {/* Badge header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name={badgeCfg?.icon} size={16} color={badgeCfg?.iconColor} strokeWidth={2.5} />
              <span
                className={`text-sm font-semibold ${badgeCfg?.text}`}
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Por encima del Total Lote máximo
              </span>
            </div>
            <Tooltip
              title="Nivel de Riesgo Asumido"
              description="ACEPTABLE: pagas hasta un 2% por encima del máximo — riesgo manejable. ELEVADO: entre 2% y 5% — el buffer de protección está significativamente comprometido. CRÍTICO: más del 5% — la operación tiene alta probabilidad de pérdida si el mercado se mueve en tu contra."
            >
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border cursor-help ${badgeCfg?.bg} ${badgeCfg?.border} ${badgeCfg?.text}`}
              >
                {riskBadge}
              </span>
            </Tooltip>
          </div>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 gap-2">
            {/* Riesgo Asumido */}
            <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.6)" }}>
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                <Tooltip
                  title="Riesgo Asumido (€)"
                  description="Diferencia entre el precio negociado y el precio máximo de seguridad. Representa el margen de protección que estás sacrificando en esta operación."
                >
                  Riesgo Asumido
                </Tooltip>
              </p>
              <p
                className="font-data font-bold text-sm"
                style={{ color: "var(--color-error)" }}
              >
                +{fmt(riesgoAsumidoTotal)}
              </p>
              {riesgoAsumidoPerTon != null && (
                <p className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  {fmtNum(riesgoAsumidoPerTon)} €/t
                </p>
              )}
            </div>

            {/* Margen Real Resultante */}
            <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.6)" }}>
              <p
                className="text-xs mb-0.5"
                style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                <Tooltip
                  title="Margen Real Resultante (€/t)"
                  description="Margen que quedará tras la venta si el metal se vende al precio benchmark esperado con el yield estimado. Si es negativo, el lote está comprando a pérdida esperada."
                >
                  Margen Real Resultante
                </Tooltip>
              </p>
              <p
                className={`font-data font-bold text-sm ${
                  margenRealResultante < 0 ? "text-red-600" : "text-slate-700"
                }`}
              >
                {fmt(margenRealResultante)}
              </p>
              {margenRealResultantePerTon != null && (
                <p className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  {fmtNum(margenRealResultantePerTon)} €/t
                </p>
              )}
              {margenRealResultante < 0 && (
                <p className="text-xs text-red-600 mt-0.5" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  ⚠ Margen comprometido
                </p>
              )}
            </div>

            {/* Buffer consumido */}
            {bufferConsumido != null && (
              <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.6)" }}>
                <p
                  className="text-xs mb-0.5"
                  style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  <Tooltip
                    title="Buffer de Riesgo Consumido (%)"
                    description="Porcentaje del buffer de protección original que ya has gastado al pagar por encima del máximo. Un buffer consumido al 100% significa que no tienes ninguna protección frente a caídas de mercado."
                  >
                    Buffer consumido
                  </Tooltip>
                </p>
                <p
                  className="font-data font-bold text-sm"
                  style={{ color: bufferConsumido >= 80 ? "var(--color-error)" : "var(--color-warning)" }}
                >
                  {fmtNum(bufferConsumido, 1)}%
                </p>
                <p className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Estás consumiendo {fmtNum(bufferConsumido, 1)}% de tu buffer
                </p>
              </div>
            )}

            {/* Punto de equilibrio */}
            {puntoEquilibrio != null && (
              <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.6)" }}>
                <p
                  className="text-xs mb-0.5"
                  style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  <Tooltip
                    title="Punto de Equilibrio del Benchmark (€/t)"
                    description="Precio mínimo al que debe cotizar el metal en el momento de la venta para que el lote no entre en pérdida con el precio negociado. Si el benchmark cae por debajo de este valor, perderás dinero."
                  >
                    Punto de equilibrio
                  </Tooltip>
                </p>
                <p className="font-data font-bold text-sm" style={{ color: "var(--color-text-primary)" }}>
                  {fmtNum(puntoEquilibrio, 0)} €/t
                </p>
                <p className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Pérdida si benchmark &lt; {fmtNum(puntoEquilibrio, 0)} €/t
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Save section ── */}
      {hasNegPrice && (
        <div className="flex flex-col gap-2">
          {showNameInput && (
            <div>
              <label
                className="block text-xs font-medium mb-1"
                style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Nombre del lote <span style={{ color: "var(--color-error)" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Ej: Lote Cobre #001"
                value={batchName}
                onChange={(e) => setBatchName(e?.target?.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{
                  borderColor: "var(--color-border)",
                  background: "var(--color-card)",
                  color: "var(--color-text-primary)",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  outline: "none",
                }}
              />
            </div>
          )}
          <Button
            variant={isAboveCeiling ? "outline" : "default"}
            fullWidth
            iconName="Save"
            iconPosition="left"
            loading={saving}
            disabled={saving || (showNameInput && !batchName?.trim())}
            onClick={handleSave}
            style={isAboveCeiling ? { borderColor: badgeCfg?.iconColor, color: badgeCfg?.iconColor } : {}}
          >
            {saving ? "Guardando..." : "Guardar lote con este precio"}
          </Button>
        </div>
      )}
    </div>
  );
}
